import { unregisterMeshScene } from './mesh-scene-registry.js';
import { disposeMeshGpu } from '../mesh/mesh-dispose.js';
import { removeMeshFromTask } from '../frame-graph/render-task.js';
import { retireGpuResources } from '../engine/gpu-resource-retirement.js';

function removeFromScene(scene, entity) {
  if ("entities" in entity) {
    const container = entity;
    for (const e of container.entities) {
      removeFromScene(scene, e);
    }
    if (container.camera && scene.camera === container.camera) {
      scene.camera = null;
    }
    const groups = container.animationGroups;
    if (groups?.length) {
      for (const g of groups) {
        spliceOut(scene.animationGroups, g);
      }
    }
    const hook = container._beforeRenderHook;
    if (hook) {
      spliceOut(scene._beforeRender, hook);
      container._beforeRenderHook = void 0;
    }
    const cleanups = container._sceneCleanups;
    if (cleanups) {
      const cleanup = cleanups.get(scene);
      if (cleanup) {
        spliceOut(scene._disposables, cleanup);
        cleanups.delete(scene);
        if (!scene._z) {
          cleanup();
        }
      }
    }
    return;
  }
  if ("_gpu" in entity && "material" in entity) {
    removeMeshFromScene(scene, entity);
    removeChildren(scene, entity);
    return;
  }
  if ("lightType" in entity) {
    spliceOut(scene.lights, entity);
    const sg = entity.shadowGenerator;
    if (sg) {
      disposeShadowGenerator(scene, sg);
    } else {
      markTopologyDirty(scene);
    }
  } else if ("fov" in entity && "nearPlane" in entity) {
    if (scene.camera === entity) {
      scene.camera = null;
    }
  } else if ("_shadowType" in entity && "_light" in entity) {
    disposeShadowGenerator(scene, entity);
  }
  detachParent(entity);
  removeChildren(scene, entity);
}
function retireMeshTeardown(scene, teardown) {
  for (const dispose of teardown) {
    const packet = dispose.p;
    if (packet) {
      packet._disposed = true;
      const owner = packet._owner;
      if (owner) {
        const index = owner.indexOf(packet);
        if (index >= 0) {
          owner.splice(index, 1);
        }
        packet._owner = void 0;
      }
    }
  }
  retireSceneGpu(scene, () => {
    for (const fn of teardown) {
      fn();
    }
  });
}
function retireSceneGpu(scene, teardown) {
  if (scene._z) {
    teardown();
    return;
  }
  retireGpuResources(scene.surface.engine, teardown);
}
function disposeShadowGenerator(scene, sg) {
  spliceOut(scene.shadowGenerators, sg);
  const light = sg._light;
  if (light && light.shadowGenerator === sg) {
    light.shadowGenerator = void 0;
  }
  markTopologyDirty(scene);
  const state = sg._shadowTaskState;
  if (state) {
    sg._shadowTaskState = void 0;
    queueTopologyRetirement(scene, () => state._task.dispose());
  }
}
function queueTopologyRetirement(scene, retirement) {
  if (!scene._pendingTopologyRetirements) {
    scene._pendingTopologyRetirements = [];
    scene._disposables.push(() => drainOnDispose(scene));
  }
  scene._pendingTopologyRetirements.push(retirement);
}
function drainOnDispose(scene) {
  const pending = scene._pendingTopologyRetirements?.splice(0);
  if (!pending?.length) {
    return;
  }
  const run = () => {
    for (const dispose of pending) {
      try {
        dispose();
      } catch {
      }
    }
  };
  const device = scene.surface.engine._device;
  if (device) {
    void device.queue.onSubmittedWorkDone().then(run, run);
  } else {
    run();
  }
}
function markTopologyDirty(scene) {
  scene._lightListVersion = (scene._lightListVersion ?? 0) + 1;
  if (scene._built) {
    scene._rebuildHook = rebuildOnNextBuild;
  }
}
async function rebuildOnNextBuild(scene) {
  const { rebuildSceneRenderables } = await import('./scene-rebuild.js');
  await rebuildSceneRenderables(scene);
}
function spliceOut(arr, item) {
  const i = arr.indexOf(item);
  if (i >= 0) {
    arr.splice(i, 1);
  }
}
function detachParent(node) {
  if (node && typeof node === "object" && "parent" in node) {
    node.parent = null;
  }
}
function removeChildren(scene, node) {
  const kids = node.children;
  if (kids?.length) {
    for (const child of [...kids]) {
      removeFromScene(scene, child);
    }
  }
}
function removeMeshFromScene(scene, mesh) {
  for (const task of scene._frameGraph._tasks) {
    task._removeMesh?.(mesh);
  }
  const fns = scene._meshDisposables.get(mesh);
  let didMutate = false;
  const teardown = [];
  if (fns) {
    didMutate = true;
    teardown.push(...fns);
    scene._meshDisposables.delete(mesh);
  }
  const auxFns = scene._meshAuxDisposables.get(mesh);
  if (auxFns) {
    didMutate = true;
    teardown.push(...auxFns);
    scene._meshAuxDisposables.delete(mesh);
  }
  const mi2 = scene.meshes.indexOf(mesh);
  if (mi2 >= 0) {
    scene.meshes.splice(mi2, 1);
    didMutate = true;
  }
  const i = scene._renderables.findIndex((r) => r.mesh === mesh);
  if (i >= 0) {
    scene._renderables.splice(i, 1);
    didMutate = true;
  }
  if (didMutate) {
    scene._renderableVersion++;
  }
  for (const group of scene._groups.values()) {
    const gi = group.indexOf(mesh);
    if (gi >= 0) {
      group.splice(gi, 1);
    }
  }
  const qi = scene._materialSwapQueue.indexOf(mesh);
  if (qi >= 0) {
    scene._materialSwapQueue.splice(qi, 1);
  }
  scene._runtimeBuilds?.remove(mesh);
  mesh.parent = null;
  for (const task of scene._frameGraph._tasks) {
    if ("_renderables" in task) {
      removeMeshFromTask(task, mesh);
    }
  }
  if (unregisterMeshScene(scene, mesh)) {
    teardown.push(() => disposeMeshGpu(mesh));
  }
  if (teardown.length) {
    retireMeshTeardown(scene, teardown);
  }
}

export { removeFromScene };
//# sourceMappingURL=scene-remove.js.map
